import {
  forwardRef,
  useCallback,
  type ComponentType,
  type ReactElement,
  type Ref,
  type ReactNode,
} from 'react';

import ru from 'date-fns/locale/ru';
import { useTheme } from 'styled-components';

import DatepickerBase, { registerLocale } from 'react-datepicker';
import type {
  ReactDatePickerProps,
} from 'react-datepicker';

import {
  StyledInput,
} from '../Input';

import { DatepickerInputMask } from './DatepickerInputMask';
import {
  StyledDatepicker,
  StyledIcon,
} from './style';
import './globalStyle';

export const outputDateFormat = 'dd.LL.yyyy';
export const timeFormat = 'HH:mm';

export const outputDateWithTimeFormat = `${outputDateFormat} ${timeFormat}`;

export const timeIntervals = 15;

registerLocale('ru', ru);

export type DatepickerAdditionalProps = {
  /**
   * Допольнительный класс
   */
  className?: string;
  /**
   * Выбранная дата
   */
  selected?: Date;
  /**
   * Выключено ли поле
   */
  disabled?: boolean;
  /**
   * Маленькое ли поле
   */
  small?: boolean;
  /**
   * Есть ли у поля ошибка
   */
  hasError?: boolean;
  /**
   * Есть ли у поля предупреждение
   */
  hasWarning?: boolean;
  /**
   * Есть ли возможность выбора времени
   */
  withTime?: boolean;
  /**
   * Текст над вариантами выбора времени
   */
  timeCaption?: string;
  /**
   * Отображать ли поле во всю ширину
   */
  block?: boolean;
  /**
   * Возможность сброса выбранной даты
   */
  isClearable?: boolean;
  /**
   * Включен ли нативный автокомплит в поле ввода дейтпикера
   */
  autoComplete?: 'on' | 'off';

  /**
   * Обработчик изменения или сброса значения
   * @param {?Date} nextDate - новое значение
   */
  onChange?: (nextDate: Date | null) => void;
  /**
   * Класс для обертки иконки
   */
  iconWrapperClassName?: string;
};

export type DatepickerProps<
  CustomModifierNames extends string = never,
  WithRange extends boolean | undefined = undefined,
> = Omit<ReactDatePickerProps<CustomModifierNames, WithRange>, 'onChange'> & DatepickerAdditionalProps;

function DatepickerInner<
  CustomModifierNames extends string = never,
  WithRange extends boolean | undefined = undefined,
>(
  {
    className = undefined,
    hasError = false,
    hasWarning = false,
    block = false,
    isClearable = false,
    small = false,
    disabled = false,
    withTime = false,
    autoComplete = 'off',
    timeCaption = 'Время',
    onChange = undefined,
    popperContainer = undefined,
    iconWrapperClassName = undefined,
    ...rest
  }: DatepickerProps<CustomModifierNames, WithRange>,
  ref: Ref<DatepickerBase<CustomModifierNames, WithRange>>,
): ReactElement {
  const { datePicker } = useTheme();
  const popperContainerFunc = useCallback(
    (props: { children: ReactNode[] }) => (
      <StyledDatepicker
        className={className}
        isSmall={small}
        isBlock
        isClearable={isClearable}
        withTime={withTime}
      >
        {popperContainer?.(props) || props.children}
      </StyledDatepicker>
    ),
    [popperContainer, className, small, isClearable, withTime],
  );

  const dpProps: Partial<ReactDatePickerProps<CustomModifierNames, WithRange>> = {
    locale: 'ru',
    dateFormat: outputDateFormat,
    placeholderText: withTime ? '__.__.____ __:__' : '__.__.____',
  };

  if (withTime) {
    dpProps.showTimeSelect = true;
    dpProps.timeFormat = timeFormat;
    dpProps.timeIntervals = timeIntervals;
    dpProps.dateFormat = outputDateWithTimeFormat;

    if (onChange) {
      // https://github.com/Hacker0x01/react-datepicker/issues/1182
      dpProps.onSelect = onChange;
    }
  } else {
    dpProps.dateFormat = outputDateFormat;
  }

  return (
    <StyledDatepicker
      className={className}
      isSmall={small}
      isBlock={block}
      isClearable={isClearable}
      withTime={withTime}
    >
      <StyledInput
        ref={ref}
        {...dpProps}
        {...rest}
        disabled={disabled}
        withTime={withTime}
        autoComplete={autoComplete}
        timeCaption={timeCaption}
        onChange={onChange}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        as={DatepickerBase as ComponentType<any>}
        $small={small}
        $hasError={hasError}
        $hasWarning={hasWarning}
        disabledKeyboardNavigation
        isClearable={isClearable}
        customInput={<DatepickerInputMask withTime={withTime} />}
        popperContainer={popperContainerFunc}
      />
      <StyledIcon className={iconWrapperClassName} isDisabled={disabled}>
        {hasError
          ? datePicker.CALENDAR_ICON_ERROR
          : hasWarning
            ? datePicker.CALENDAR_ICON_WARNING
            : datePicker.CALENDAR_ICON}
      </StyledIcon>
    </StyledDatepicker>
  );
}

export const Datepicker = forwardRef(DatepickerInner) as <
  CustomModifierNames extends string = never,
  WithRange extends boolean | undefined = undefined,
>(p: DatepickerProps<CustomModifierNames, WithRange> & {
  ref?: Ref<DatepickerBase<CustomModifierNames, WithRange>>;
}) => ReactElement;
